import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { injectIntl, FormattedMessage } from 'react-intl'
import cx from 'classnames'
import _ from 'lodash'

import MapControls from './components/MapControls'
import ControlRow from './components/ControlRow'

import { LOCALE_PREFIX } from './constants'

import hocConnect from './hocConnect'
import './style.scss'

@injectIntl
@hocConnect
export default class GoogleMap extends Component {
    static propTypes = {
        activeAddress: PropTypes.object,
        activeAddressZoom: PropTypes.bool,
        addresses: PropTypes.array,
        googleMapChangeSatelliteView: PropTypes.func.isRequired,
        googleMapChangeStreetViewUrl: PropTypes.func.isRequired,
        googleMapChangeUrl: PropTypes.func.isRequired,
        googleMapChangeZoom: PropTypes.func.isRequired,
        googleMapCheckboxChange: PropTypes.func.isRequired,
        googleMapClose: PropTypes.func.isRequired,
        googleMapInit: PropTypes.func.isRequired,
        googleMapToggleExpandedStreetView: PropTypes.func.isRequired,
        id: PropTypes.string.isRequired,
        intl: PropTypes.object,
        mapUrl: PropTypes.string,
        satelliteView: PropTypes.bool,
        showBranch: PropTypes.bool,
        showRegistered: PropTypes.bool,
        showShops: PropTypes.bool,
        streetViewExpanded: PropTypes.bool,
        streetViewUrl: PropTypes.string,
    }

    static defaultProps = {
        activeAddressZoom: false,
        satelliteView: false,
        showBranch: true,
        showRegistered: true,
        showShops: true,
        streetViewExpanded: false,
    }

    componentDidMount() {
        const {
            id,
            googleMapInit,
            addresses,
            activeAddress,
            activeAddressZoom,
            showBranch,
            showRegistered,
            showShops,
            satelliteView,
        } = this.props

        googleMapInit(
            id,
            addresses,
            activeAddress,
            activeAddressZoom,
            showRegistered,
            showBranch,
            showShops,
            satelliteView
        )
    }

    shouldComponentUpdate(nextProps) {
        const {
            activeAddress,
            addresses,
            activeAddressZoom,
            showRegistered,
            showBranch,
            showShops,
            streetViewExpanded,
            mapUrl,
            streetViewUrl,
            satelliteView,
        } = this.props

        if (activeAddressZoom !== nextProps.activeAddressZoom) return true
        if (showRegistered !== nextProps.showRegistered) return true
        if (showBranch !== nextProps.showBranch) return true
        if (showShops !== nextProps.showShops) return true
        if (streetViewExpanded !== nextProps.streetViewExpanded) return true
        if (mapUrl !== nextProps.mapUrl) return true
        if (streetViewUrl !== nextProps.streetViewUrl) return true
        if (satelliteView !== nextProps.satelliteView) return true
        if (!_.isEqual(activeAddress, nextProps.activeAddress)) return true
        if (!_.isEqual(addresses, nextProps.addresses)) return true

        return false
    }

    componentDidUpdate(prevProps) {
        const { activeAddressZoom, activeAddress, addresses, showBranch, showRegistered, showShops, satelliteView } =
            this.props

        if (prevProps.activeAddressZoom !== activeAddressZoom) this.handleMapUrlChange()
        if (prevProps.showBranch !== showBranch) this.handleMapUrlChange()
        if (prevProps.showRegistered !== showRegistered) this.handleMapUrlChange()
        if (prevProps.showShops !== showShops) this.handleMapUrlChange()
        if (prevProps.satelliteView !== satelliteView) this.handleMapUrlChange()
        if (!_.isEqual(addresses, prevProps.addresses)) this.handleMapUrlChange()
        if (!_.isEqual(activeAddress, prevProps.activeAddress)) {
            this.handleMapUrlChange()
            this.handleStreetViewUrlChange()
        }
    }

    componentWillUnmount() {
        const { id, googleMapClose } = this.props

        googleMapClose(id)
    }

    handleMapUrlChange = () => {
        const {
            googleMapChangeUrl,
            id,
            addresses,
            activeAddress,
            activeAddressZoom,
            showRegistered,
            showBranch,
            showShops,
            satelliteView,
        } = this.props

        googleMapChangeUrl(
            id,
            addresses,
            activeAddress,
            activeAddressZoom,
            showRegistered,
            showBranch,
            showShops,
            satelliteView
        )
    }

    handleStreetViewUrlChange = () => {
        const { googleMapChangeStreetViewUrl, id, activeAddress } = this.props

        googleMapChangeStreetViewUrl(id, activeAddress)
    }

    handleZoomChange = (activeAddressZoom) => {
        const { id, googleMapChangeZoom } = this.props

        googleMapChangeZoom(id, activeAddressZoom)
    }

    handleMapViewChange = () => {
        const { id, googleMapChangeSatelliteView } = this.props

        googleMapChangeSatelliteView(id)
    }

    handleCheckBoxChange = (e, chkBoxName) => {
        const { id, googleMapCheckboxChange } = this.props

        googleMapCheckboxChange(id, chkBoxName, e.target.checked)
    }

    handleExpandClick = () => {
        const { id, streetViewExpanded, googleMapToggleExpandedStreetView } = this.props

        googleMapToggleExpandedStreetView(id, !streetViewExpanded)
    }

    render() {
        const {
            id,
            intl,
            addresses,
            activeAddress,
            showBranch,
            showRegistered,
            showShops,
            streetViewExpanded,
            mapUrl,
            streetViewUrl,
        } = this.props

        const actAddGpsX = _.get(activeAddress, 'gpsX')
        const actAddGpsY = _.get(activeAddress, 'gpsY')
        const actAddValue = _.get(activeAddress, 'value')

        return (
            <div className={'google-map-wrapper'}>
                <div className={'main-row'}>
                    <div className={cx('map', streetViewExpanded && 'collapsed')}>
                        <span className={'no-map-available-msg'}>
                            <FormattedMessage id={`${LOCALE_PREFIX}.unavailableMap`} />
                        </span>
                        {((!_.isNil(actAddGpsX) && !_.isNil(actAddGpsY)) || !_.isNil(actAddValue)) &&
                            !_.isEmpty(addresses) && (
                                <div className={'map-wrapper'}>
                                    <img src={mapUrl} alt={''} />
                                    <MapControls
                                        addresses={addresses}
                                        handleMapViewChange={this.handleMapViewChange}
                                        handleZoomChange={this.handleZoomChange}
                                        // Show warning when entity has many addresses and the google map url is very long
                                        // Max unencoded url length is 8192 which means we should show warning at around ~7500 characters
                                        // Url is encoded which means it can be larger than 8192 chars
                                        showWarning={mapUrl && mapUrl.length >= 8250}
                                        id={id}
                                        intl={intl}
                                    />
                                </div>
                            )}
                    </div>
                    <div className={cx('street-view', streetViewExpanded && 'expanded')}>
                        <span className={'no-streetview-available-msg'}>
                            <FormattedMessage id={`${LOCALE_PREFIX}.unavailableStreetView`} />
                        </span>
                        {((!_.isNil(actAddGpsX) && !_.isNil(actAddGpsY)) || !_.isNil(actAddValue)) && (
                            <div className={'street-view-wrapper'}>
                                <img src={streetViewUrl} alt={''} />
                            </div>
                        )}
                    </div>
                </div>
                <ControlRow
                    activeAddress={activeAddress}
                    handleCheckBoxChange={this.handleCheckBoxChange}
                    showBranch={showBranch}
                    showRegistered={showRegistered}
                    showShops={showShops}
                />
            </div>
        )
    }
}
